import { Injectable } from '@angular/core';

import { environment } from 'env/environment';

export const CORE_HOST_QUERY_PARAM = 'coreHost';

const HOST_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

@Injectable({
  providedIn: 'root'
})
export class UrlService {
  private static readonly STORAGE_COREAPIURL_KEY: string = 'CoreApiUrl';

  public static resolveCoreUrlFromHost(hostLabel: string, baseCoreUrl = environment.coreApiUrl): string | null {
    if (!HOST_LABEL_PATTERN.test(hostLabel)) {
      return null;
    }

    try {
      const parsed = new URL(baseCoreUrl);
      const hostname = parsed.hostname.toLowerCase();
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return null;
      }

      const dot = hostname.indexOf('.');
      if (dot <= 0) {
        return null;
      }

      parsed.hostname = `${hostLabel}${hostname.slice(dot)}`;
      return parsed.href.replace(/\/+$/, '');
    }
    catch {
      return null;
    }
  }

  public constructor() {
    this.applyCoreHostFromSearch(window.location.search);
  }

  public applyCoreHostFromSearch(search: string): void {
    const coreHost = new URLSearchParams(search).get(CORE_HOST_QUERY_PARAM);
    if (!coreHost) {
      return;
    }

    const coreUrl = UrlService.resolveCoreUrlFromHost(coreHost);
    if (coreUrl) {
      this.setServerApiUrl(coreUrl);
    }
  }

  public setServerApiUrl(url: string): void {
    if (url) {
      url = url.replace(/\/+$/, '');
      localStorage.setItem(UrlService.STORAGE_COREAPIURL_KEY, url);
    }
  }

  public getServerApiUrl(): string {
    return localStorage.getItem(UrlService.STORAGE_COREAPIURL_KEY) ??
      environment.coreApiUrl;
  }

  /** Whether `url` points at the same Core host as the one this session is using. */
  public isCurrentServerApiUrl(url: string): boolean {
    const normalize = (value: string) => {
      try {
        return new URL(value).host.toLowerCase();
      }
      catch {
        return value.replace(/\/+$/, '').toLowerCase();
      }
    };

    return normalize(url) === normalize(this.getServerApiUrl());
  }

  public buildServerApiUrl(...parts: string[]): string {
    return [this.getServerApiUrl()]
      .concat(parts)
      .join('/');
  }

  public buildGameServerApiUrl(...parts: string[]): string {
    const baseUrl = environment.overrideGameApiUrl ?? this.buildServerApiUrl('api/nova');

    return [baseUrl]
      .concat(parts)
      .join('/');
  }

  public buildIdentityServerApiUrl(...parts: string[]): string {
    return this.buildServerApiUrl('Login', ...parts);
  }
}
